// HU-30 (MG-ILE-58) · tarea 30-4. Administración › Catálogos.
//
// Spec `05_analisis_tecnico/Farmacovigilancia/ui/MG-ILE-58.md`. Dos paneles: a la izquierda los
// catálogos que se mantienen aquí (con su etiqueta y sus conteos, del servidor) y la nota de los
// que se mantienen en otro sitio (C8); a la derecha los valores del catálogo elegido, activos y
// retirados, con las acciones que entrega el servidor por valor.
//
//   · Agregar y editar en un `Modal`. El duplicado lo decide el servidor (RN-53): su 409 se pinta
//     bajo «Etiqueta» con el texto del criterio y el catálogo no cambia (C2).
//   · Retirar pide confirmación y dice qué pasa: el valor deja de ofrecerse y los casos que lo
//     usan no cambian (C3, AF §10.3). Reactivar vuelve a ofrecerlo con el mismo código (C5).
//   · No hay «Eliminar»: un valor se retira, nunca se borra (RN-53, RNF-04).
//   · Tras cada acción se vuelven a pedir el catálogo y el índice (§8.1 R3).
//
// EL CÓDIGO DEL VALOR. La spec dice que lo deriva el servidor de la etiqueta, pero el contrato
// vigente (`ValorDeCatalogoEntrada`) todavía lo EXIGE (V-07, REVISION_FINAL E.11). Hasta que se
// decida, el alta lo pide con el patrón del contrato; la pantalla no inventa la regla de derivación.
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Archive, ArchiveRestore, ListPlus, Pencil, Plus, RotateCcw, ShieldOff, Tags } from 'lucide-react';
import {
  Alert, Button, Card, DataTable, EmptyState, Field, Input, Mono, Modal, PageHeader, StatusBadge,
} from '../components/ui';
import type { ColumnaDeTabla, FilaDeTabla } from '../components/ui';
import { agregarValor, catalogoParaAdministrar, editarValor, indiceDeCatalogos } from '../api/catalogos';
import type { CatalogoParaAdministrar, IndiceDeCatalogos, ValorDelCatalogo } from '../api/catalogos';
import { aFallo } from '../api/cliente';
import type { Fallo } from '../api/cliente';
import { useErroresAlSalir } from '../lib/useErroresAlSalir';
import { puedeDescartar } from '../lib/descarte';

const SIN_ACCESO = 'No tienes acceso a esta opción';
const CODIGO_VALIDO = /^[a-z0-9_]+$/;

const COLUMNAS: ColumnaDeTabla[] = [
  { key: 'nombre', label: 'Etiqueta', className: 'w-[40%]' },
  { key: 'codigo', label: 'Código', className: 'w-[20%]' },
  { key: 'orden', label: 'Orden', numeric: true, className: 'w-[10%]' },
  { key: 'estado', label: 'Estado', className: 'w-[15%]' },
  { key: 'acciones', label: '' },
];

type ModoDelFormulario = { modo: 'alta' } | { modo: 'edicion'; valor: ValorDelCatalogo };

function FormularioDeValor({ abierto, modo, catalogo, onCerrar, onGuardado }: {
  abierto: boolean;
  modo: ModoDelFormulario | null;
  catalogo: CatalogoParaAdministrar | null;
  onCerrar: () => void;
  onGuardado: (mensaje: string) => void;
}) {
  const [nombre, setNombre] = useState('');
  const [orden, setOrden] = useState('0');
  const [codigo, setCodigo] = useState('');
  const [errorDelServidor, setErrorDelServidor] = useState<string | null>(null);
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [guardando, setGuardando] = useState(false);
  const edicion = modo?.modo === 'edicion' ? modo.valor : null;

  const errores = {
    nombre: !nombre.trim() ? 'Escribe la etiqueta.' : (nombre.trim().length > 200 ? 'La etiqueta admite hasta 200 caracteres.' : undefined),
    orden: !/^\d{1,3}$/.test(orden.trim()) ? 'El orden es un número entero entre 0 y 999.' : undefined,
    codigo: edicion ? undefined : (!CODIGO_VALIDO.test(codigo) ? 'Usa minúsculas, dígitos y guion bajo, sin espacios (p. ej. ovinos).' : undefined),
  };
  const { errorDe, tocar, tocarTodo, reiniciar } = useErroresAlSalir(errores);

  useEffect(() => {
    if (!abierto) return;
    setNombre(edicion?.nombre ?? '');
    setOrden(String(edicion?.orden ?? 0));
    setCodigo('');
    setErrorDelServidor(null);
    setFallo(null);
    reiniciar();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo al abrir
  }, [abierto]);

  const sucio = edicion
    ? nombre !== edicion.nombre || orden !== String(edicion.orden)
    : Boolean(nombre || codigo || orden !== '0');

  const guardar = async () => {
    if (!catalogo || !tocarTodo()) return;
    setGuardando(true);
    setFallo(null);
    setErrorDelServidor(null);
    try {
      if (edicion) {
        const guardado = await editarValor(catalogo.catalogo, edicion.codigo, { nombre: nombre.trim(), orden: Number(orden) });
        onGuardado(`«${guardado.nombre}» actualizado en ${catalogo.etiqueta}.`);
      } else {
        const nuevo = await agregarValor(catalogo.catalogo, { codigo, nombre: nombre.trim(), orden: Number(orden) });
        onGuardado(`«${nuevo.nombre}» agregado a ${catalogo.etiqueta}: ya se ofrece en el formulario.`);
      }
    } catch (e) {
      const f = aFallo(e);
      // HU-30-C2: el duplicado va BAJO la etiqueta, con el texto del servidor.
      if (f.cuerpo?.codigo === 'etiqueta_duplicada') setErrorDelServidor(f.message);
      else setFallo(f);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal open={abierto} onClose={onCerrar} sucio={sucio} data-testid="catalogos-form"
      eyebrow={`Administración · Catálogos · ${catalogo?.etiqueta ?? ''}`}
      title={edicion ? 'Editar valor' : 'Agregar valor'} icon={edicion ? Pencil : ListPlus}
      footer={(
        <>
          <Button variant="secondary" onClick={() => { if (puedeDescartar(sucio)) onCerrar(); }} data-testid="catalogos-form-cancelar">Cancelar</Button>
          <Button onClick={() => { guardar(); }} disabled={guardando} data-testid="catalogos-form-guardar">
            {guardando ? 'Guardando…' : (edicion ? 'Guardar' : 'Agregar')}
          </Button>
        </>
      )}>
      <div className="space-y-4">
        {fallo && <Alert tone="danger" title="No se pudo guardar" data-testid="catalogos-error-envio" detalleTecnico={fallo.detalleTecnico}>{fallo.message}</Alert>}
        <Field label="Etiqueta" required error={errorDelServidor ?? errorDe('nombre')} onTocar={() => tocar('nombre')} campo="nombre"
          hint="Como se ve en el formulario. Dos valores no pueden llamarse igual.">
          <Input data-testid="catalogos-form-etiqueta" value={nombre} maxLength={200}
            onChange={(e) => { setNombre(e.target.value); setErrorDelServidor(null); }} />
        </Field>
        {!edicion && (
          <Field label="Código" required error={errorDe('codigo')} onTocar={() => tocar('codigo')} campo="codigo"
            hint="Lo que se guarda en los casos. No cambia nunca.">
            <Input data-testid="catalogos-form-codigo" value={codigo} maxLength={60} className="font-mono"
              onChange={(e) => setCodigo(e.target.value)} />
          </Field>
        )}
        {edicion && (
          <p className="text-meta text-ink-soft">Código <Mono>{edicion.codigo}</Mono>: no cambia, y los casos ya registrados conservan su valor.</p>
        )}
        <Field label="Orden" error={errorDe('orden')} onTocar={() => tocar('orden')} campo="orden" hint="Posición en el desplegable (0 a 999).">
          <Input data-testid="catalogos-form-orden" type="number" min={0} max={999} step={1} inputMode="numeric" value={orden}
            onChange={(e) => setOrden(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

function ConfirmarRetiro({ valor, catalogo, onCerrar, onGuardado }: {
  valor: ValorDelCatalogo | null;
  catalogo: CatalogoParaAdministrar | null;
  onCerrar: () => void;
  onGuardado: (mensaje: string) => void;
}) {
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [guardando, setGuardando] = useState(false);
  useEffect(() => { setFallo(null); }, [valor]);

  const retirar = async () => {
    if (!valor || !catalogo) return;
    setGuardando(true);
    setFallo(null);
    try {
      const retirado = await editarValor(catalogo.catalogo, valor.codigo, { activo: false });
      onGuardado(`«${retirado.nombre}»: ${retirado.texto_del_estado}. Ya no se ofrece en casos nuevos.`);
    } catch (e) {
      setFallo(aFallo(e));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal open={valor !== null} onClose={onCerrar} tone="warning" icon={Archive} data-testid="catalogos-modal-retirar"
      eyebrow={`Administración · Catálogos · ${catalogo?.etiqueta ?? ''}`} title={`¿Retirar «${valor?.nombre ?? ''}»?`}
      footer={(
        <>
          <Button variant="secondary" onClick={onCerrar}>Cancelar</Button>
          {/* Primario y no `danger`: el blanco sobre rose-500 del catálogo no llega a 4,5:1 (lineamiento, par 4); el tono del cuadro ya dice que es una baja. */}
          <Button onClick={() => { retirar(); }} disabled={guardando} data-testid="catalogos-confirmar-retirar">
            {guardando ? 'Retirando…' : 'Sí, retirar'}
          </Button>
        </>
      )}>
      <div className="space-y-3">
        <p>Deja de ofrecerse en los formularios de casos nuevos. Retirar un valor no altera los casos que ya lo usan.</p>
        <p className="text-ink-soft">No se borra: se puede reactivar y vuelve con el mismo código.</p>
        {fallo && <Alert tone="danger" title="No se pudo retirar" data-testid="catalogos-error-envio" detalleTecnico={fallo.detalleTecnico}>{fallo.message}</Alert>}
      </div>
    </Modal>
  );
}

export default function Catalogos() {
  const [indice, setIndice] = useState<IndiceDeCatalogos | null>(null);
  const [elegido, setElegido] = useState<string | null>(null);
  const [catalogo, setCatalogo] = useState<CatalogoParaAdministrar | null>(null);
  const [cargando, setCargando] = useState(true);
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [falloDeAccion, setFalloDeAccion] = useState<Fallo | null>(null);
  const [exito, setExito] = useState<string | null>(null);
  const [formulario, setFormulario] = useState<ModoDelFormulario | null>(null);
  const [retirarA, setRetirarA] = useState<ValorDelCatalogo | null>(null);
  const [reactivando, setReactivando] = useState<string | null>(null);

  const vivo = useRef(true);
  useEffect(() => {
    vivo.current = true;
    return () => { vivo.current = false; };
  }, []);

  /** El índice y, si hay uno elegido (o el primero), sus valores. */
  const cargar = useCallback(async (cual: string | null) => {
    setCargando(true);
    setFallo(null);
    try {
      const nuevoIndice = await indiceDeCatalogos();
      if (!vivo.current) return;
      setIndice(nuevoIndice);
      const codigo = cual ?? nuevoIndice.catalogos[0]?.catalogo ?? null;
      setElegido(codigo);
      if (codigo) {
        const valores = await catalogoParaAdministrar(codigo);
        if (vivo.current) setCatalogo(valores);
      }
    } catch (e) {
      if (vivo.current) setFallo(aFallo(e));
    } finally {
      if (vivo.current) setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(null); }, [cargar]);

  const elegir = (codigo: string) => {
    setExito(null);
    setFalloDeAccion(null);
    setCatalogo(null);
    cargar(codigo);
  };

  const hecho = (mensaje: string) => {
    setFormulario(null);
    setRetirarA(null);
    setFalloDeAccion(null);
    setExito(mensaje);
    cargar(elegido);
  };

  const reactivar = async (valor: ValorDelCatalogo) => {
    if (!catalogo) return;
    setExito(null);
    setFalloDeAccion(null);
    setReactivando(valor.codigo);
    try {
      const reactivado = await editarValor(catalogo.catalogo, valor.codigo, { activo: true });
      hecho(`«${reactivado.nombre}»: ${reactivado.texto_del_estado}. Se vuelve a ofrecer.`);
    } catch (e) {
      setFalloDeAccion(aFallo(e));
    } finally {
      setReactivando(null);
    }
  };

  const abrir = (accion: () => void) => { setExito(null); setFalloDeAccion(null); accion(); };

  const encabezado = (
    <PageHeader
      eyebrow="Administración · Catálogos"
      hu="HU-30 · RN-09 / RN-53"
      title="Catálogos"
      subtitle="Las listas de valores de los formularios. Retirar un valor no cambia los casos que ya lo usan."
      icon={Tags}
    />
  );

  if (fallo?.estado === 403) {
    return (
      <>
        {encabezado}
        <EmptyState icon={ShieldOff} title={SIN_ACCESO} data-testid="catalogos-sin-permiso"
          description="Los catálogos los mantiene el administrador del sistema." />
      </>
    );
  }

  const puedeAgregar = Boolean(catalogo?.acciones_permitidas.includes('agregar'));

  const celda = (fila: FilaDeTabla, columna: ColumnaDeTabla): ReactNode => {
    const v = fila as unknown as ValorDelCatalogo;
    switch (columna.key) {
      case 'nombre': return <span className="font-medium">{v.nombre}</span>;
      case 'codigo': return <span data-testid="catalogos-tabla-codigo"><Mono className="text-meta">{v.codigo}</Mono></span>;
      case 'orden': return <span data-testid="catalogos-tabla-orden">{v.orden}</span>;
      case 'estado': return <StatusBadge tone={v.activo ? 'success' : 'neutral'} data-testid="catalogos-tabla-estado">{v.texto_del_estado}</StatusBadge>;
      case 'acciones':
        return (
          <span className="flex flex-wrap justify-end gap-1.5">
            {v.acciones_permitidas.includes('editar') && (
              <Button size="sm" variant="ghost" icon={Pencil} data-testid="catalogos-tabla-editar" aria-label={`Editar ${v.nombre}`}
                onClick={() => abrir(() => setFormulario({ modo: 'edicion', valor: v }))}>Editar</Button>
            )}
            {v.acciones_permitidas.includes('retirar') && (
              <Button size="sm" variant="ghost" icon={Archive} data-testid="catalogos-tabla-retirar" aria-label={`Retirar ${v.nombre}`}
                onClick={() => abrir(() => setRetirarA(v))}>Retirar</Button>
            )}
            {v.acciones_permitidas.includes('reactivar') && (
              <Button size="sm" variant="ghost" icon={ArchiveRestore} data-testid="catalogos-tabla-reactivar" aria-label={`Reactivar ${v.nombre}`}
                disabled={reactivando === v.codigo} onClick={() => { reactivar(v); }}>Reactivar</Button>
            )}
          </span>
        );
      default: return null;
    }
  };

  return (
    <>
      {encabezado}

      {exito && <div className="mb-4"><Alert tone="success" data-testid="catalogos-exito" onCerrar={() => setExito(null)}>{exito}</Alert></div>}
      {falloDeAccion && (
        <div className="mb-4">
          <Alert tone="danger" title="No se pudo completar" data-testid="catalogos-error-envio" detalleTecnico={falloDeAccion.detalleTecnico}
            onCerrar={() => setFalloDeAccion(null)}>{falloDeAccion.message}</Alert>
        </div>
      )}

      {fallo && (
        <div className="space-y-3 mb-4">
          <Alert tone="danger" title="No se pudieron cargar los catálogos" data-testid="catalogos-error" detalleTecnico={fallo.detalleTecnico}>
            {fallo.message}{fallo.estado === 0 ? ' Revisa tu conexión e intenta refrescar.' : ''}
          </Alert>
          <Button variant="secondary" icon={RotateCcw} onClick={() => { cargar(elegido); }} data-testid="catalogos-reintentar">Reintentar</Button>
        </div>
      )}

      {cargando && !indice && !fallo && (
        <p data-testid="catalogos-cargando" role="status" className="text-body text-ink-soft">Cargando los catálogos…</p>
      )}

      {indice && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] items-start">
          <div className="space-y-4">
            <Card title="Catálogos" pad={false}>
              <ul data-testid="catalogos-lista" aria-label="Catálogos" className="p-2 space-y-1">
                {indice.catalogos.map((c) => (
                  <li key={c.catalogo}>
                    <Button variant="ghost" size="sm" className="w-full" aria-pressed={c.catalogo === elegido}
                      data-testid="catalogos-lista-item" onClick={() => elegir(c.catalogo)}>
                      <span className="flex w-full flex-wrap items-center justify-between gap-2 text-left">
                        <span className={c.catalogo === elegido ? 'text-ink font-semibold' : ''}>{c.etiqueta}</span>
                        <StatusBadge data-testid="catalogos-lista-conteo">{`${c.valores_activos} activos · ${c.valores_retirados} retirados`}</StatusBadge>
                      </span>
                    </Button>
                  </li>
                ))}
              </ul>
            </Card>
            {indice.no_mantenidos_aqui.length > 0 && (
              <Alert tone="info" title="Se mantienen en otro sitio" data-testid="catalogos-nota-no-mantenidos">
                <ul className="space-y-0.5">
                  {indice.no_mantenidos_aqui.map((n) => <li key={n.nombre}><b>{n.nombre}</b> · {n.donde_se_mantiene}</li>)}
                </ul>
              </Alert>
            )}
          </div>

          <Card title={catalogo?.etiqueta ?? ''} pad={false}
            actions={puedeAgregar && (
              <Button size="sm" icon={Plus} onClick={() => abrir(() => setFormulario({ modo: 'alta' }))} data-testid="catalogos-agregar">
                Agregar valor
              </Button>
            )}>
            {!catalogo && cargando && (
              <p data-testid="catalogos-cargando" role="status" className="p-5 text-body text-ink-soft">Cargando los valores…</p>
            )}
            {catalogo && (
              <DataTable sinMarco columns={COLUMNAS} data-testid="catalogos-tabla" testidDeFila="catalogos-tabla-fila"
                rows={catalogo.valores.map((v) => ({ ...v, id: v.codigo }))} renderCell={celda}
                empty={(
                  <EmptyState icon={ListPlus} data-testid="catalogos-vacio" title={`${catalogo.etiqueta} todavía no tiene valores`}
                    action={puedeAgregar ? (
                      <Button icon={Plus} onClick={() => abrir(() => setFormulario({ modo: 'alta' }))}>Agregar valor</Button>
                    ) : undefined} />
                )} />
            )}
          </Card>
        </div>
      )}

      <FormularioDeValor abierto={formulario !== null} modo={formulario} catalogo={catalogo}
        onCerrar={() => setFormulario(null)} onGuardado={hecho} />
      <ConfirmarRetiro valor={retirarA} catalogo={catalogo} onCerrar={() => setRetirarA(null)} onGuardado={hecho} />
    </>
  );
}
